import { Fragment, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";

import CartBackImg from "../assets/images/cart-back.png";
import BlogListSkeleton from "../components/blog/BlogListSkeleton";
import { useStaticPageContent } from "../hooks/useStaticPageContent";
import { staticContentDefaults } from "../data/staticContentDefaults";
import { getStaticContentImage } from "../utils/staticContentImages";
import CmsRichText from "../components/common/CmsRichText";
import {
  getBlogBanner,
  getBlogCategory,
  getBlogDate,
  getBlogExcerpt,
  getBlogTheme,
} from "../utils/blogDisplayUtils";

const BlogCardIcon = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 6.75C9.1 6.75 6.75 9.1 6.75 12C6.75 14.9 9.1 17.25 12 17.25C14.9 17.25 17.25 14.9 17.25 12C17.25 9.1 14.9 6.75 12 6.75Z"
      stroke={color}
      strokeWidth="1.8"
    />
    <path
      d="M12 4.5V6.75M12 17.25V19.5M19.5 12H17.25M6.75 12H4.5M17.01 6.99L15.4 8.6M8.6 15.4L6.99 17.01M17.01 17.01L15.4 15.4M8.6 8.6L6.99 6.99"
      stroke={color}
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

const Blog = () => {
  const pageContent = useStaticPageContent("blog");
  const banner = pageContent?.banner || staticContentDefaults.blog.banner;
  const intro = pageContent?.intro || staticContentDefaults.blog.intro;
  const emptyText =
    pageContent?.emptyText || staticContentDefaults.blog.emptyText;
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  useEffect(() => {
    const loadPosts = async () => {
      try {
        const response = await axios.get(
          `${import.meta.env.VITE_APP_BASE_URL}/api/blogs/public`
        );
        const items = response?.data?.data;
        setPosts(Array.isArray(items) ? items : []);
      } catch {
        setError("Unable to load blog posts.");
      } finally {
        setLoading(false);
      }
    };

    void loadPosts();
  }, []);

  return (
    <Fragment>
      <div className="banner-section cart-banner contact">
        <img src={getStaticContentImage(banner.image, CartBackImg)} alt="Blog banner" />
        <h1>
          <CmsRichText as="span" value={banner.title} />
        </h1>
      </div>

      <div className="contact-sec blog-sec">
        <div className="container">
          <CmsRichText as="p" className="text-center" value={intro} />

          {loading ? <BlogListSkeleton count={6} /> : null}
          {error ? <p className="text-center">{error}</p> : null}

          {!loading && !error && posts.length === 0 ? (
            <CmsRichText as="p" className="text-center" value={emptyText} />
          ) : null}

          {!loading && !error && posts.length > 0 ? (
            <div className="blog-card-grid blog-collection-grid">
              {posts.map((post, index) => {
                const theme = getBlogTheme(index);
                const excerpt = getBlogExcerpt(post);
                const publishedOn = getBlogDate(post);
                const banner = getBlogBanner(post);

                return (
                  <Link
                    key={post.slug}
                    to={`/blog/${post.slug}`}
                    className="blog-card-grid-item"
                  >
                    <div className="blog-card-banner-wrap">
                      {banner ? (
                        <img src={banner} alt="" className="blog-card-banner" />
                      ) : (
                        <div
                          className="blog-card-banner blog-card-banner--placeholder"
                          style={{ backgroundColor: theme.bg }}
                        />
                      )}
                    </div>

                    <div className="blog-card-body">
                      <div
                        className="blog-card-icon"
                        style={{ backgroundColor: theme.bg }}
                      >
                        <BlogCardIcon color={theme.color} />
                      </div>
                      <p
                        className="blog-card-category"
                        style={{ color: theme.color }}
                        title={getBlogCategory(post, theme)}
                      >
                        {getBlogCategory(post, theme)}
                      </p>
                      <h2 className="blog-card-title">{post.title}</h2>
                      {excerpt ? (
                        <p className="blog-card-excerpt">{excerpt}</p>
                      ) : null}
                      <div className="blog-card-meta">
                        {publishedOn ? (
                          <span>
                            <i className="fa fa-calendar-o" aria-hidden="true" />{" "}
                            {publishedOn}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          ) : null}
        </div>
      </div>
    </Fragment>
  );
};

export default Blog;
